import { useSearch } from "wouter";
import { XCircle, ArrowLeft, Phone, Mail, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PaymentDeclined() {
  const searchString = useSearch();
  const params = new URLSearchParams(searchString);

  const reason = params.get("reason") || "Your payment could not be processed.";
  const amount = params.get("amount");
  const invoiceNumber = params.get("invoiceNumber");

  return (
    <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Declined Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center">
            <XCircle className="w-12 h-12 text-red-400" />
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-[#111827] border border-gray-800 rounded-xl p-8 text-center">
          <h1 className="text-2xl font-bold text-white mb-2">Payment Declined</h1>
          <p className="text-gray-400 mb-6">
            {reason}
          </p>

          {/* Payment Details */}
          {amount && (
            <div className="bg-[#0a0f1a] rounded-lg p-6 mb-6 text-left space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-sm">Amount</span>
                <span className="text-white text-xl font-bold">${parseFloat(amount).toFixed(2)}</span>
              </div>
              {invoiceNumber && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-sm">Reference</span>
                  <span className="text-gray-200 text-xs font-mono">{invoiceNumber}</span>
                </div>
              )}
            </div>
          )}

          {/* Suggestions */}
          <div className="bg-[#0a0f1a] rounded-lg p-5 mb-6 text-left">
            <p className="text-gray-300 text-sm font-medium mb-3">Common reasons for declined payments:</p>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-gray-500 mt-0.5">•</span>
                Insufficient funds in the account
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gray-500 mt-0.5">•</span>
                Incorrect card number, expiration, or CVV
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gray-500 mt-0.5">•</span>
                Card issuer blocked the transaction
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gray-500 mt-0.5">•</span>
                Billing address does not match card on file
              </li>
            </ul>
          </div>

          <p className="text-gray-500 text-sm mb-6">
            Please verify your payment information and try again, or contact us for assistance.
          </p>

          {/* Actions */}
          <div className="flex flex-col gap-3 mb-6">
            <Button
              onClick={() => window.history.back()}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </Button>
          </div>

          {/* Contact Info */}
          <div className="border-t border-gray-800 pt-6 space-y-3">
            <p className="text-gray-400 text-sm">Need help? Contact us:</p>
            <div className="flex justify-center gap-6">
              <a href="tel:+17029131109" className="flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm">
                <Phone className="w-4 h-4" />
                (702) 913-1109
              </a>
              <a href="mailto:support@unifipro.ai" className="flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm">
                <Mail className="w-4 h-4" />
                Email Support
              </a>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-gray-600 text-xs mt-6">
          Powered by UnifiPro AI &bull; Secure Payment Processing
        </p>
      </div>
    </div>
  );
}
